import { CalculationMethod } from "constants/charts.enums";
import { format } from "echarts";
import { TooltipOption } from "echarts/types/dist/shared";
import { tooltipRow } from "entities/charts/eva/translators/rigFracTooltip";
import { EvaChartStates } from "types/factory";
import { removeCount } from "utils/removeCountFromTitle";

import { DataEnum, DataType } from "models/groupBy";

import { applyRemoveUUID } from "components/charts/utils";
import { formatNumber } from "components/charts/utils/precisionUtils";

// ECharts hands tooltip formatters an untyped params bag (object or array).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TooltipParams = any;

type SeriesLike = { y?: unknown[] };

// Keyed by category name, not dataIndex: the translator reorders/filters
// categories (xAxisSort, axis sync) but tooltips carry the raw response.
export const buildStackedBarTotals = (
  categories: unknown[],
  series: SeriesLike[]
): Map<string, number> => {
  const totals = new Map<string, number>();
  categories.forEach((category, i) => {
    const total = series.reduce((sum, s) => sum + (Number(s.y?.[i]) || 0), 0);
    totals.set(String(category), total);
  });
  return totals;
};

interface StackedBarItemTooltipOptions {
  groupByLabel: string;
  metricLabel: string;
  bucketLabel: string;
  totalsByCategory: Nullable<Map<string, number>>;
  formatValue: (value: Maybe<number | "">) => string;
}

export const buildStackedBarItemTooltipFormatter =
  ({
    groupByLabel,
    metricLabel,
    bucketLabel,
    totalsByCategory,
    formatValue
  }: StackedBarItemTooltipOptions) =>
  (params: TooltipParams): string => {
    const item = Array.isArray(params) ? params[0] : params;
    if (!item) {
      return "";
    }

    const raw = Array.isArray(item.value) ? item.value[1] : item.value;
    const formatted = formatValue(raw);

    const total = totalsByCategory?.get(String(item.name));
    const metricValue =
      total === undefined ? formatted : `${formatted} (of ${formatValue(total)})`;

    // Stacked bar requests always send showWellCount: true, so labels carry "(n) ".
    const segment = removeCount(applyRemoveUUID(String(item.seriesName ?? "")));

    return (
      (groupByLabel
        ? tooltipRow(groupByLabel, segment)
        : `<div><b>${format.encodeHTML(segment)}</b></div>`) +
      tooltipRow(metricLabel, metricValue) +
      (item.name && bucketLabel ? tooltipRow(bucketLabel, String(item.name)) : "")
    );
  };

const countFormat = new Intl.NumberFormat();

// The horizontal response layout is not swapped: xAxis.title is always the bucket label.
export const buildStackedBarItemTooltipOptions = (
  states: EvaChartStates,
  valueDataType: DataType
): TooltipOption => {
  const {
    layout,
    series,
    histogramToggle,
    calculationMethod,
    groupByLabel,
    defaultSignificantFigures,
    useStrictRounding
  } = states;
  const method = calculationMethod?.properties?.method;
  const hasTotals =
    (method === CalculationMethod.Count || method === CalculationMethod.Sum) &&
    !histogramToggle?.active;

  const formatValue = (value: Maybe<number | "">): string => {
    if (value === null || value === undefined || value === "") return "";
    return method === CalculationMethod.Count || valueDataType === DataEnum.Integer
      ? countFormat.format(Math.round(value))
      : formatNumber(value, defaultSignificantFigures, useStrictRounding, valueDataType);
  };

  return {
    trigger: "item",
    confine: true,
    enterable: true,
    extraCssText: "border-style:none;",
    className: "chart-tooltip-container",
    showDelay: 500,
    hideDelay: 0,
    show: true,
    axisPointer: { type: "none" },
    position: function (point, _, dom) {
      const xPos = point[0];
      const yPos =
        point[1] - (dom as HTMLDivElement).offsetHeight > 48
          ? point[1] - (dom as HTMLDivElement).offsetHeight
          : 48;
      return [xPos, yPos];
    },
    formatter: buildStackedBarItemTooltipFormatter({
      groupByLabel: groupByLabel ?? "",
      metricLabel: layout?.yAxis?.title || "",
      // Histogram x-axis is the group-by itself, so a bucket row would repeat the group row.
      bucketLabel: histogramToggle?.active ? "" : layout?.xAxis?.title || "",
      totalsByCategory: hasTotals
        ? buildStackedBarTotals(layout?.xAxis?.data ?? [], series ?? [])
        : null,
      formatValue
    })
  };
};
